import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Link,
  Stack,
  Avatar,
  Button,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';

import { useModal } from 'src/hooks/useModal';

import { updateTicket } from 'src/services/ticket.service';
import { fetchTagsByDepartment } from 'src/services/tag.service';
import { deleteUserPermission } from 'src/services/ticket.permissions.service';

import Iconify from 'src/components/iconify';

import { styles } from '../styles';
import TicketTimeLine from './ticketTimeLine';
import AddUpdateTags from '../../tags/modals/AddUpdateTags';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';

ManagementSidebar.propTypes = {
  ticket: PropTypes.object,
  permissionUsers: PropTypes.array,
  onRefresh: PropTypes.func.isRequired,
  onUpdateTicketData: PropTypes.func,
  onAddUsers: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  statusesList: PropTypes.array,
  departmentUsers: PropTypes.array,
  hasUpdatePermission: PropTypes.bool,
  canViewCollab: PropTypes.bool,
};

export default function ManagementSidebar({
  ticket,
  permissionUsers = [],
  onRefresh,
  onUpdateTicketData,
  onAddUsers,
  fetchUser,
  statusesList = [],
  departmentUsers = [],
  hasUpdatePermission,
  canViewCollab,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();

  const [noteText, setNoteText] = useState(ticket?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [systemTags, setSystemTags] = useState([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);

  const [tagToDelete, setTagToDelete] = useState(null);

  const canWrite = hasUpdatePermission;

  useEffect(() => {
    setNoteText(ticket?.description || '');
  }, [ticket?.description]);

  const loadTags = useCallback(async () => {
    if (!ticket?.departmentId) return;
    try {
      const data = await fetchTagsByDepartment(ticket.departmentId);
      setSystemTags(data);
    } catch (e) {
      console.error(e);
    }
  }, [ticket?.departmentId]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const isUserDeletable = (uid) =>
    ticket && uid !== ticket.createdBy && uid !== ticket.assignedTo;

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUserPermission({ ticketId: ticket.id, userId });
      await onRefresh();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveNewTag = async () => {
    const finalTagName = selectedTag ? selectedTag.name : newTagName;
    if (!finalTagName.trim()) return;

    const updated = [
      ...(ticket.tags || []),
      {
        value: newTagValue,
        tag: {
          id: selectedTag?.id || null,
          name: finalTagName,
        },
      },
    ];
    const updatedTicket = await updateTicket({ ...ticket, tags: updated });
    setIsAddingTag(false);
    setNewTagName('');
    setNewTagValue('');
    setSelectedTag(null);

    if (onUpdateTicketData) {
      onUpdateTicketData(updatedTicket);
    } else {
      await onRefresh();
    }
  };
  const handleOpenCreateAttribute = (name) => {
    showModal({
      title: t('pages.tagsPage.addNewTag'),
      Component: AddUpdateTags,
      props: {
        hideModal,
        initialData: { name, departmentId: ticket.departmentId },
        handleFetchTags: (newTag) => {
          if (newTag) {
            setSelectedTag(newTag);
            setNewTagName(newTag.name);
            loadTags();
          }
        },
      },
    });
  };

  const hasMatch = useMemo(() => {
    if (!newTagName.trim()) return true;
    return systemTags.some((a) =>
      a.name.toLowerCase().includes(newTagName.toLowerCase())
    );
  }, [newTagName, systemTags]);

  const handleConfirmDeleteTag = async () => {
    if (!tagToDelete) return;
    try {
      const updatedTags = ticket.tags.filter(
        (tag) => !(tag.id === tagToDelete.id && tag.tag.name === tagToDelete.tag.name)
      );
      const updatedTicket = await updateTicket({ ...ticket, tags: updatedTags });

      if (onUpdateTicketData) {
        onUpdateTicketData(updatedTicket);
      } else {
        await onRefresh();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setTagToDelete(null);
    }
  };

  const handleSaveNote = async () => {
    setIsSaving(true);
    try {
      const updatedTicket = await updateTicket({ ...ticket, description: noteText });

      if (onUpdateTicketData) {
        onUpdateTicketData(updatedTicket);
      } else {
        await onRefresh();
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!ticket) return null;

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2, ...styles.scrollbarCustom }}>
      <Stack spacing={3}>
        {/* 1. INTERNAL MEMO (Guarded background color & input text) */}
        <Box
          sx={{
            ...styles.cardBase,
            bgcolor: isDark ? alpha(theme.palette.warning.main, 0.05) : '#FFFCDB',
            border: isDark
              ? `1px solid ${alpha(theme.palette.warning.main, 0.15)}`
              : 'none',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                component="img"
                src="/assets/icons/Tickets/notes.svg"
                sx={{
                  width: 27,
                  height: 27,
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                  color: 'text.primary',
                }}
              >
                {t('ticket.notes')}
              </Typography>
            </Stack>
            {canWrite && noteText !== (ticket.description || '') && !isSaving && (
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={handleSaveNote}
                  sx={{ color: 'success.main' }}
                >
                  <Iconify icon="solar:check-read-bold" width={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setNoteText(ticket.description)}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon="solar:close-circle-bold" width={18} />
                </IconButton>
              </Stack>
            )}
          </Stack>
          <TextField
            multiline
            fullWidth
            variant="standard"
            value={noteText}
            placeholder={t('tickettt.writeNotePlaceholder')}
            onChange={(e) => setNoteText(e.target.value)}
            InputProps={{
              disableUnderline: false, // Standard bottom writing line enabled
              sx: {
                fontSize: '0.8rem',
                color: 'text.primary',
                // Styled soft underline that lights up on hover/focus
                '&:before': {
                  borderBottomColor: isDark
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottomColor: isDark
                    ? alpha(theme.palette.warning.main, 0.3)
                    : 'rgba(0,0,0,0.12)',
                },
                '&:after': {
                  borderBottomColor: theme.palette.warning.main,
                },
              },
              readOnly: !canWrite,
            }}
          />
        </Box>

        {/* 2. TAGS MANAGEMENT (Theme-aware card background) */}
        <Box sx={{ ...styles.cardBase, p: 2.5, backgroundColor: 'background.neutral' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box
                component="img"
                src="/assets/icons/Tickets/attribute.svg"
                sx={{
                  width: 27,
                  height: 27,
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                  color: 'text.primary', // Theme-aware primary text
                }}
              >
                {t('ticket.Tag')}
              </Typography>
            </Stack>
            {canWrite && !isAddingTag && (
              <Tooltip title={t('ticket.addTag')} placement="top">
                <IconButton
                  onClick={() => setIsAddingTag(true)}
                  size="small"
                  sx={{
                    color: '#00A76F',
                    bgcolor: 'rgba(0, 167, 111, 0.08)',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                  }}
                >
                  <Iconify icon="solar:add-square-bold-duotone" width={24} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Stack spacing={0}>
            {ticket.tags?.map((tag) => (
              <Stack
                key={tag.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover .delete-tag': { opacity: canWrite ? 1 : 0 },
                }}
              >
                <Stack spacing={0.2} sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                    }}
                  >
                    {tag.tag?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: 'text.primary', // Theme-aware primary text
                      wordBreak: 'break-word',
                    }}
                  >
                    {tag.value || '—'}
                  </Typography>
                </Stack>
                {canWrite && (
                  <IconButton
                    className="delete-tag"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTagToDelete(tag);
                    }}
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: 'error.main',
                      bgcolor: 'rgba(255, 72, 66, 0.08)',
                      '&:hover': { bgcolor: 'rgba(255, 72, 66, 0.16)' },
                    }}
                  >
                    <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                  </IconButton>
                )}
              </Stack>
            ))}

            {isAddingTag && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'background.neutral',
                  borderRadius: '12px',
                }}
              >
                <Stack spacing={1}>
                  <Autocomplete
                    freeSolo
                    options={systemTags}
                    getOptionLabel={(option) => option.name || option}
                    onInputChange={(e, v) => setNewTagName(v)}
                    onChange={(e, nv) => setSelectedTag(nv)}
                    noOptionsText={null} // Hides the "No options" popup entirely to keep the "+ Add Attribute" button visible
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label={t('ticket.attributeName')}
                        variant="standard"
                        autoFocus
                        error={!hasMatch && newTagName !== '' && !selectedTag}
                        helperText={
                          !hasMatch && newTagName !== '' && !selectedTag
                            ? t('formControl.mustCreateAttribute')
                            : ''
                        }
                        InputProps={{
                          ...p.InputProps,
                          sx: {
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: 'text.primary',
                          },
                        }}
                      />
                    )}
                  />
                  {!hasMatch && newTagName !== '' && (
                    <Link
                      component="button"
                      variant="caption"
                      onMouseDown={() => handleOpenCreateAttribute(newTagName)}
                      sx={{
                        fontWeight: 800,
                        textDecoration: 'none',
                        color: '#00A76F',
                        textAlign: 'left',
                        mt: 0.5,
                      }}
                    >
                      {`+ ${t('buttons.add')} "${newTagName}"`}
                    </Link>
                  )}
                  <TextField
                    label={t('ticket.value')}
                    variant="standard"
                    fullWidth
                    value={newTagValue}
                    onChange={(e) => setNewTagValue(e.target.value)}
                    InputProps={{ sx: { fontSize: '0.85rem', color: 'text.primary' } }}
                    sx={{ mt: 1 }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ mt: 1 }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setIsAddingTag(false);
                        setSelectedTag(null);
                      }}
                      color="inherit"
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      size="small"
                      onClick={handleSaveNewTag}
                      variant="contained"
                      disabled={
                        !selectedTag &&
                        !systemTags.some(
                          (s) => s.name.toLowerCase() === newTagName.toLowerCase()
                        )
                      }
                      sx={{
                        bgcolor: '#00A76F',
                        textTransform: 'none',
                        fontWeight: 800,
                        '&:hover': { bgcolor: '#008F5D' },
                      }}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>

        {/* 3. GUEST LIST (Theme-aware card background) */}
        {canViewCollab && (
          <Box
            sx={{
              ...styles.cardBase,
              p: 2.5,
              mt: 3,
              backgroundColor: 'background.neutral',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Box
                  component="img"
                  src="/assets/icons/Tickets/permission.svg"
                  sx={{
                    width: 27,
                    height: 27,
                    filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                    transition: 'filter 0.25s',
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    letterSpacing: '0.05em',
                    color: 'text.primary', // Theme-aware primary text
                  }}
                >
                  {t('ticket.userPermissionList')}
                </Typography>
              </Stack>
              {canWrite && (
                <Tooltip title={t('ticket.addUsersToTicket')} placement="top">
                  <IconButton
                    onClick={onAddUsers}
                    size="small"
                    sx={{
                      color: '#00A76F',
                      bgcolor: 'rgba(0, 167, 111, 0.08)',
                      borderRadius: '8px',
                      '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.16)' },
                    }}
                  >
                    <Iconify icon="solar:add-square-bold-duotone" width={24} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ height: '165px', overflowY: 'auto', ...styles.scrollbarCustom }}>
              <Stack spacing={0}>
                {permissionUsers.map((u) => {
                  // FIXED: Creators and assignees are the only non-deletable users now
                  const canDelete = isUserDeletable(u.id);
                  return (
                    <Stack
                      key={u.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover .delete-user-btn': { opacity: canWrite ? 1 : 0 },
                      }}
                    >
                      <Avatar
                        src={u.profilePic}
                        sx={{ width: 38, height: 38 }}
                        imgProps={{ loading: 'lazy' }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800, color: 'text.primary' }}
                        >
                          {u.firstName} {u.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary', // Mapped to secondary text for hierarchy
                            display: 'block',
                            fontSize: '0.7rem',
                          }}
                        >
                          Email: {u.email}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary', // Mapped to secondary text for hierarchy
                            display: 'block',
                            fontSize: '0.7rem',
                          }}
                        >
                          Role: {(u.role || '').replace('ROLE_', '')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {(u.permissions?.[0] || ticket.userPermission?.[u.id]?.[0]) && (
                          <Box
                            sx={{
                              ...styles.tagPillStyle,
                              bgcolor: '#00A76F',
                              border: 'none',
                              px: 1,
                              boxShadow: 'none',
                            }}
                          >
                            <Typography
                              sx={{ color: '#FFF', fontSize: '0.65rem', fontWeight: 900 }}
                            >
                              Read
                            </Typography>
                          </Box>
                        )}
                        {(u.permissions?.[1] || ticket.userPermission?.[u.id]?.[1]) && (
                          <Box
                            sx={{
                              ...styles.tagPillStyle,
                              bgcolor: '#00A76F',
                              border: 'none',
                              px: 1,
                              boxShadow: 'none',
                            }}
                          >
                            <Typography
                              sx={{ color: '#FFF', fontSize: '0.65rem', fontWeight: 900 }}
                            >
                              Write
                            </Typography>
                          </Box>
                        )}
                        {canWrite && (
                          <IconButton
                            className="delete-user-btn"
                            size="small"
                            disabled={!canDelete}
                            onClick={() => handleDeleteUser(u.id)}
                            sx={{
                              ml: 0.5,
                              color: canDelete ? '#FF0000' : 'text.disabled',
                              opacity: 0,
                              transition: 'opacity 0.2s ease-in-out',
                            }}
                          >
                            <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}

        {/* 4. AUDIT TRAIL (Theme-aware card background) */}
        <Box
          sx={{
            ...styles.cardBase,
            p: 2.5,
            mt: 3,
            backgroundColor: 'background.neutral',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box
                component="img"
                src="/assets/icons/Tickets/interactions.svg"
                sx={{
                  width: 27,
                  height: 27,
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  letterSpacing: '0.05em',
                  color: 'text.primary', // Theme-aware primary text
                }}
              >
                {t('ticket.Interactions')}
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ maxHeight: '230px', overflowY: 'auto', ...styles.scrollbarCustom }}>
            <TicketTimeLine
              list={ticket.comments?.filter((c) => c.messageType === 'UPDATE') || []}
              ticketData={ticket}
              fetchUser={fetchUser}
              statusesList={statusesList}
              departmentUsers={departmentUsers}
            />
          </Box>
        </Box>
      </Stack>

      <ConfirmationDialog
        open={Boolean(tagToDelete)}
        onClose={() => setTagToDelete(null)}
        onConfirm={handleConfirmDeleteTag}
        title={t('ticket.confirmDeleteTagTitle')}
        message={t('ticket.confirmDeleteTagMessage', { tagName: tagToDelete?.tag?.name })}
      />
    </Box>
  );
}
